/**
 * Cambiar la contraseña del portal, desde la barra superior.
 *
 * Solo para una cuenta propia del portal (Caso A) y solo si el tenant permite
 * entrar con contraseña: con un SSO obligatorio no hay contraseña que cambiar, y
 * quien entra con su usuario del ERP cambia la del ERP, no esta.
 *
 * Pide la contraseña actual: una sesión abierta en un equipo ajeno no debe
 * bastar para quedarse con la cuenta.
 */
import { useState } from 'react'
import { KeyRound, Loader2 } from 'lucide-react'
import { Modal } from '@/components/ds/Modal'
import { useSSOConfig } from '@/modules/auth/hooks/useAuth'
import { extractErrorMessage } from '@/lib/errorUtils'
import { usePortalChangePassword } from '../hooks/useEmployeePortal'

const campo = 'w-full px-3 py-2 text-sm bg-input-bg border border-input-border rounded-lg text-content ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring'

export function CambiarContrasena() {
  const { data: ssoConfig } = useSSOConfig()
  const [abierto, setAbierto] = useState(false)
  if (ssoConfig?.portal_password_login_allowed === false) return null

  return (
    <>
      <button
        type="button"
        onClick={() => setAbierto(true)}
        className="inline-flex items-center gap-1.5 text-sm text-content-muted hover:text-content transition-colors"
        aria-label="Cambiar la contraseña del portal"
        title="Cambiar la contraseña"
      >
        <KeyRound size={15} aria-hidden="true" />
        <span className="hidden md:inline">Contraseña</span>
      </button>
      {abierto && <ModalCambio onClose={() => setAbierto(false)} />}
    </>
  )
}

function ModalCambio({ onClose }: { onClose: () => void }) {
  const cambiar = usePortalChangePassword()
  const [actual, setActual] = useState('')
  const [nueva, setNueva] = useState('')
  const [repetida, setRepetida] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [listo, setListo] = useState(false)

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (nueva.length < 8) { setError('La contraseña nueva debe tener al menos 8 caracteres.'); return }
    if (nueva !== repetida) { setError('Las contraseñas nuevas no coinciden.'); return }
    try {
      await cambiar.mutateAsync({ current_password: actual, new_password: nueva })
      setListo(true)
    } catch (err) {
      setError(extractErrorMessage(err, 'No se pudo cambiar la contraseña.'))
    }
  }

  return (
    <Modal open onClose={onClose} title="Cambiar la contraseña" size="sm">
      {listo ? (
        <div role="status" className="text-sm text-content">
          <p>Contraseña actualizada. El próximo ingreso ya usa la nueva.</p>
          <div className="flex justify-end mt-4">
            <button onClick={onClose} className="px-4 py-2 text-sm font-semibold rounded-lg bg-primary text-on-primary hover:bg-primary-hover">
              Cerrar
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={guardar} className="space-y-3">
          {error && <p className="text-sm text-danger-solid" role="alert">{error}</p>}
          <div>
            <label htmlFor="portal-pass-actual" className="block text-xs font-medium text-content-muted mb-1">Contraseña actual</label>
            <input id="portal-pass-actual" type="password" autoComplete="current-password" required
              value={actual} onChange={e => setActual(e.target.value)} className={campo} />
          </div>
          <div>
            <label htmlFor="portal-pass-nueva" className="block text-xs font-medium text-content-muted mb-1">Contraseña nueva</label>
            <input id="portal-pass-nueva" type="password" autoComplete="new-password" required minLength={8}
              value={nueva} onChange={e => setNueva(e.target.value)} className={campo} />
          </div>
          <div>
            <label htmlFor="portal-pass-repetida" className="block text-xs font-medium text-content-muted mb-1">Repetir la contraseña nueva</label>
            <input id="portal-pass-repetida" type="password" autoComplete="new-password" required
              value={repetida} onChange={e => setRepetida(e.target.value)} className={campo} />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft">
              Cancelar
            </button>
            <button type="submit" disabled={cambiar.isPending}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-60">
              {cambiar.isPending && <Loader2 size={14} className="animate-spin" aria-hidden="true" />}
              Guardar
            </button>
          </div>
        </form>
      )}
    </Modal>
  )
}
